"use client";

import { useLocale } from "next-intl";
import { useOrganization } from "@/hooks/useOrganization";
import { useTeamsContext } from "@/hooks/useTeamsContext";

export { AUTHENTICATED_ROUTE_PREFIXES, buildOrgTeamPath, stripOrgTeamPrefix } from "./orgTeamPath.shared";

import { buildOrgTeamPath } from "./orgTeamPath.shared";

/**
 * Hook that provides a `build` function pre-bound to the current org/team context.
 * Includes the locale prefix so `router.push(build("/files"))` stays in the
 * same `[locale]` layout and avoids a full server re-render.
 *
 * **Important:** `build()` already prepends the locale (e.g. `/en/…`).
 * Use it with `next/navigation`'s `useRouter`, NOT `@/i18n/navigation`'s,
 * which would add the locale a second time (resulting in `/en/en/…`).
 *
 * @example
 * import { useRouter } from "next/navigation";
 * const { build } = useOrgTeamPath();
 * router.push(build("/files"));
 */
export function useOrgTeamPath() {
  const { currentOrganizationId } = useOrganization();
  const { currentTeamId } = useTeamsContext();
  const locale = useLocale();

  return {
    build: (path: string) => {
      if (!currentOrganizationId || !currentTeamId) return path;
      return `/${locale}${buildOrgTeamPath(path, { orgId: currentOrganizationId, teamId: currentTeamId })}`;
    },
    orgId: currentOrganizationId,
    teamId: currentTeamId,
  };
}
